import {useState} from 'react';
import {Product} from '../../products/models/product.model';
import {ProductAPIDataSource} from '../../products/api/ProductAPIDataSource';
import {mapProductDTOToModel} from '../../products/mappers/product.mapper';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

interface ProductDetails {
  [productId: number]: Product | null;
}

interface UseVisitProductDetailsReturn {
  productDetails: ProductDetails;
  loading: boolean;
  error: string | null;
  fetchProductDetails: (productIds: number[]) => Promise<void>;
}

/**
 * Custom hook to fetch product details for visit items
 * Handles multiple product IDs and provides loading/error states
 */
export const useVisitProductDetails = (): UseVisitProductDetailsReturn => {
  const [productDetails, setProductDetails] = useState<ProductDetails>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProductDetails = async (productIds: number[]) => {
    if (!productIds || productIds.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Filter out already fetched products
      const newProductIds = productIds.filter(id => !productDetails[id]);

      if (newProductIds.length === 0) {
        setLoading(false);
        return;
      }

      // Fetch all products in parallel
      const productPromises = newProductIds.map(async (productId) => {
        try {
          const productDTO = await ProductAPIDataSource.getById(productId);
          return {
            id: productId,
            product: mapProductDTOToModel(productDTO)
          };
        } catch (err) {
          console.warn(`Failed to fetch product ${productId}:`, err);
          return {
            id: productId,
            product: null
          };
        }
      });

      const results = await Promise.all(productPromises);

      // Update state with new product details
      const newProductDetails = {...productDetails};
      results.forEach(({id, product}) => {
        newProductDetails[id] = product;
      });

      setProductDetails(newProductDetails);
    } catch (err) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(err);
      setError(appError.message);
      errorHandlingService.handleError(err); // For notifications/logging
    } finally {
      setLoading(false);
    }
  };

  return {
    productDetails,
    loading,
    error,
    fetchProductDetails
  };
};
